/**
 * Tryout setup API contracts for the Vue 3 administrative setup workflow.
 */

import { apiClient } from './client'
import type {
  TryoutRegistrationQuestion,
  TryoutRegistrationQuestionOption,
  TryoutRegistrationQuestionType,
  TryoutRegistrationRole,
  TryoutRegistrationSession,
} from './tryoutRegistration'

export interface TryoutSetupClub {
  id: string
  name: string
  logo?: string
  cardImage?: string
}

export interface TryoutSetupSeason {
  id: string
  name: string
}

export interface TryoutSetupEventTag {
  id: string
  text: string
  icon: string
  color: string
}

export interface TryoutSetupEventTags {
  tagList: string[]
  tags: Record<string, TryoutSetupEventTag>
}

export interface TryoutSetupEvent {
  id: string
  seasonId: string
  title: string
  description?: string
  gender?: 'Unspecified' | 'Mixed' | 'Male' | 'Female' | string
  eventNoun?: string
  sessionNoun?: string
  participantPronoun?: string
  participantNoun?: string
  groupPronoun?: string
  spaceNoun?: string
  staffTier1Noun?: string
  staffTier2Noun?: string
  staffTier3Noun?: string
  staffTier4Noun?: string
  staffTier5Noun?: string
  staffTier6Noun?: string
  matchNoun?: string
  periodNoun?: string
  coachNoun?: string
  trainingNoun?: string
  startDate?: string
  endDate?: string
  offerDeadline?: string
  minSelectorsForConfidence?: number
  minDataForConfidence?: number
  allowJournal?: boolean
  active?: boolean
  onlineReg?: boolean
  registrationCutoff?: string | null
  allowEarlyFeedback?: boolean
  allowJuniors?: boolean
  juniorCutoff?: string | null
  juniorAge?: number
  enforceJuniorCommsRule?: boolean
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  participantCounter?: number
  roles?: Record<string, boolean>
  tags?: TryoutSetupEventTags | TryoutSetupEventTag[] | Record<string, TryoutSetupEventTag>
  attributes?: TryoutSetupAttribute[]
}

export interface TryoutSetupMessage {
  id?: string
  name: 'parentInfo' | 'juniorExclusion' | string
  message: string
}

export interface TryoutSetupFee {
  id?: string | number
  description: string
  amount: number
  currency: string
  type?: number | string
  category?: string
  legacyType?: number
  active?: boolean
  order?: number
}

export interface TryoutSetupAttribute {
  id?: string
  title: string
  description?: string
  units?: string
  type: 'int' | 'float' | 'string' | 'boolean' | 'date' | string
  order: number
  archived?: boolean
}

export interface TryoutSetupSpace {
  id?: string
  name: string
  comment?: string
  description?: string
  type?: 'space' | string
  order?: number
  archived?: boolean
}

export interface TryoutSetupTeam {
  id: string
  name: string
  gender?: string
  division?: string
  color?: string
  selected?: boolean
  order?: number
  participantCount?: number
}

export interface TryoutSetupStaffMember {
  id: string
  firstName?: string
  lastName?: string
  name?: string
  email?: string
  isVerified?: boolean
  admin?: boolean
  programcoach?: boolean
  headcoach?: boolean
  assistantcoach?: boolean
  selector?: boolean
  support?: boolean
}

export type TryoutSetupEmailTemplateName = 'registrationEmail' | 'offerEmail' | 'noofferEmail' | string

export interface TryoutSetupEmailTemplate {
  id?: string
  name: TryoutSetupEmailTemplateName
  message: string
}

export interface TryoutSetupValidationIssue {
  code: string
  message: string
  panel?: string
  severity?: 'error' | 'warning'
}

export interface TryoutSetupValidation {
  canPublish: boolean
  issues: TryoutSetupValidationIssue[]
}

export interface TryoutSetupCriterionLevel {
  value: 0 | 1 | 2 | 3 | 4 | 5 | number
  label: string
  description: string
  feedback?: TryoutSetupCriterionLevelFeedback[]
}

export interface TryoutSetupCriterionLevelFeedback {
  id?: string
  category?: string
  text: string
}

export interface TryoutSetupCriterion {
  id: string
  title?: string
  description: string
  furtherInfo?: string
  order?: number
  context?: Array<'individual' | 'group' | string>
  type?: 'slider' | 'counter' | 'value' | 'rate' | string
  scoringMode?: 'simple' | 'feedback' | string
  scoringAggregation?: 'categoryAverage' | string
  feedback?: TryoutSetupRubricFeedback
  levels?: TryoutSetupCriterionLevel[]
  level0?: string
  level1?: string
  level2?: string
  level3?: string
  level4?: string
  level5?: string
  counterTypes?: Array<Record<string, unknown>>
}

export interface TryoutSetupRoleCriterionLink {
  roleId: string
  criterionId: string
  weight: number
  order?: number
}

export interface TryoutSetupRubricOwner {
  type: 'sport' | 'club' | 'event' | string
  id: string
  name?: string
  eventType?: string
  startDate?: string
  endDate?: string
  [key: string]: unknown
}

export interface TryoutSetupRubricPublishedBy {
  id?: string
  name?: string
  email?: string
  [key: string]: unknown
}

export interface TryoutSetupRubricVersion {
  id: string
  version?: number
  status?: 'draft' | 'published' | 'archived' | string
  active?: boolean
  publishedAt?: string
  publishedBy?: TryoutSetupRubricPublishedBy | string | null
  [key: string]: unknown
}

export interface TryoutSetupRubricLevel {
  value: 0 | 1 | 2 | 3 | 4 | 5 | number
  label: string
  description: string
  [key: string]: unknown
}

export interface TryoutSetupRubricFeedbackItem {
  id?: string
  label: string
  description?: string
  levelValue: 1 | 2 | 3 | 4 | 5 | number
  order?: number
  origin?: string
  owner?: TryoutSetupRubricOwner
  [key: string]: unknown
}

export interface TryoutSetupRubricFeedbackCategory {
  id?: string
  name: string
  order?: number
  origin?: string
  owner?: TryoutSetupRubricOwner
  items?: TryoutSetupRubricFeedbackItem[]
  [key: string]: unknown
}

export interface TryoutSetupRubricFeedback {
  aggregation?: 'categoryAverage' | string
  categories?: TryoutSetupRubricFeedbackCategory[]
  [key: string]: unknown
}

export interface TryoutSetupRubricCriterion {
  id?: string
  title?: string
  description: string
  furtherInfo?: string
  context?: Array<'individual' | 'group' | string>
  type?: 'slider' | 'counter' | 'value' | 'rate' | string
  scoringMode?: 'simple' | 'feedback' | string
  scoringAggregation?: 'categoryAverage' | string
  order?: number
  active?: boolean
  archived?: boolean
  origin?: string
  owner?: TryoutSetupRubricOwner
  levels?: TryoutSetupRubricLevel[]
  feedback?: TryoutSetupRubricFeedback
  [key: string]: unknown
}

export interface TryoutSetupRubric {
  criterionIds?: string[]
  editVersion?: string
  id: string
  name: string
  description?: string
  sportId?: string
  clubId?: string
  eventId?: string
  ownerType?: 'sport' | 'club' | 'event' | string
  ownerId?: string
  owner?: TryoutSetupRubricOwner
  sourceOwner?: TryoutSetupRubricOwner
  sourceRubricId?: string
  inheritedFrom?: TryoutSetupRubricOwner
  level?: string
  purpose?: string
  active?: boolean
  status?: string
  version?: TryoutSetupRubricVersion
  versions?: TryoutSetupRubricVersion[]
  criteria?: TryoutSetupRubricCriterion[]
  updatedAt?: string
  [key: string]: unknown
}

export interface TryoutSetupRubricsResponse {
  rubrics: TryoutSetupRubric[]
  availableRubrics?: TryoutSetupRubric[]
  validation?: TryoutSetupValidation
}

export interface TryoutSetupRubricResponse {
  rubric: TryoutSetupRubric | null
  validation?: TryoutSetupValidation
}

export interface TryoutSetupRubricLoadOptions {
  status?: 'draft' | 'published' | string
  versionId?: string
}

export interface TryoutSetupRubricInheritRequest {
  sourceRubricId: string
  sourceOwnerType?: 'sport' | 'peakBody' | 'club' | 'event' | string
}

export interface TryoutSetupRubricUpdateRequest {
  editVersion?: string
  rubric: Partial<TryoutSetupRubric>
}

export interface TryoutSetupBundleResponse {
  club: TryoutSetupClub
  season: TryoutSetupSeason
  event: TryoutSetupEvent
  sessions: TryoutRegistrationSession[]
  criteria?: TryoutSetupCriterion[]
  rubrics?: TryoutSetupRubric[]
  availableRubrics?: TryoutSetupRubric[]
  questions: TryoutRegistrationQuestion[]
  roles: TryoutRegistrationRole[]
  roleCriteria?: TryoutSetupRoleCriterionLink[]
  fees?: TryoutSetupFee[]
  attributes?: TryoutSetupAttribute[]
  spaces?: TryoutSetupSpace[]
  teams?: TryoutSetupTeam[]
  staff?: TryoutSetupStaffMember[]
  emailTemplates?: TryoutSetupEmailTemplate[]
  messages: TryoutSetupMessage[]
  validation: TryoutSetupValidation
  unsupportedPanels?: TryoutSetupUnsupportedPanel[]
}

export interface TryoutSetupUnsupportedPanel {
  key: string
  title: string
  requiredEndpoint: string
  responseShape: string
}

export interface TryoutSetupDetailsUpdateRequest {
  title: string
  description: string
  gender?: string
  eventNoun: string
  sessionNoun: string
  participantPronoun: string
  groupPronoun: string
  spaceNoun: string
  staffTier1Noun: string
  staffTier2Noun: string
  staffTier3Noun: string
  staffTier4Noun: string
  staffTier5Noun: string
  staffTier6Noun: string
  matchNoun: string
  periodNoun: string
  coachNoun: string
  trainingNoun: string
  startDate: string
  endDate: string
  offerDeadline?: string
  minSelectorsForConfidence?: number
  minDataForConfidence?: number
  allowJournal?: boolean
  tags?: TryoutSetupEventTags
}

export interface TryoutSetupPublicationUpdateRequest {
  active: boolean
  onlineReg: boolean
  registrationCutoff?: string | null
  allowEarlyFeedback?: boolean
  contactName?: string
  contactEmail?: string
  contactPhone?: string
}

export interface TryoutSetupQuestionUpdateRequest {
  questions: Array<{
    id?: string
    question: string
    order: number
    context: 'trial-registration' | import('./programSettings').ProgramQuestionContext
    type: TryoutRegistrationQuestionType
    required: boolean
    default?: unknown
    options?: TryoutRegistrationQuestionOption[]
    settings?: {
      multiple?: boolean
      publishToJournal?: boolean
    }
  }>
}

export interface TryoutSetupChildSafetyUpdateRequest {
  allowJuniors: boolean
  juniorCutoff?: string | null
  juniorAge?: number
  enforceJuniorCommsRule?: boolean
  messages: Array<Pick<TryoutSetupMessage, 'name' | 'message'>>
}

export interface TryoutSetupFeesResponse {
  fees: TryoutSetupFee[]
  validation: TryoutSetupValidation
}

export interface TryoutSetupFeesUpdateRequest {
  fees: TryoutSetupFee[]
}

export interface TryoutSetupAttributesResponse {
  attributes: TryoutSetupAttribute[]
  validation?: TryoutSetupValidation
}

export interface TryoutSetupAttributeResponse {
  attribute?: TryoutSetupAttribute
  attributes?: TryoutSetupAttribute[]
  validation?: TryoutSetupValidation
}

export interface TryoutSetupAttributeUpdateRequest {
  title: string
  description?: string
  units?: string
  type: string
  order: number
  archived?: boolean
}

export interface TryoutSetupAttributeOrderUpdateRequest {
  attributeIds: string[]
}

export interface TryoutSetupAttributesUpdateRequest {
  attributes: Array<TryoutSetupAttributeUpdateRequest & { id?: string; localId?: string }>
  deletedAttributeIds?: string[]
}

export interface TryoutSetupSpacesResponse {
  spaces: TryoutSetupSpace[]
  validation?: TryoutSetupValidation
}

export interface TryoutSetupSpacesUpdateRequest {
  spaces: TryoutSetupSpace[]
  deletedSpaceIds?: string[]
}

export interface TryoutSetupTeamsResponse {
  teams: TryoutSetupTeam[]
  validation?: TryoutSetupValidation
}

export interface TryoutSetupTeamsUpdateRequest {
  teams: TryoutSetupTeam[]
}

export interface TryoutSetupStaffResponse {
  staff: TryoutSetupStaffMember[]
  validation?: TryoutSetupValidation
}

export interface TryoutSetupStaffUpdateRequest {
  staff: TryoutSetupStaffMember[]
}

export interface TryoutSetupEmailsResponse {
  emailTemplates: TryoutSetupEmailTemplate[]
  validation?: TryoutSetupValidation
}

export interface TryoutSetupEmailsUpdateRequest {
  emailTemplates: TryoutSetupEmailTemplate[]
}

export interface TryoutSetupSessionsResponse {
  sessions: TryoutRegistrationSession[]
  criteria?: TryoutSetupCriterion[]
  validation: TryoutSetupValidation
}

export interface TryoutSetupSessionsUpdateRequest {
  sessions: Array<Omit<TryoutRegistrationSession, 'id'> & {
    id?: string
    messageTemplates?: Record<string, string>
  }>
}

export interface TryoutSetupCriteriaResponse {
  criteria: TryoutSetupCriterion[]
  validation: TryoutSetupValidation
}

export interface TryoutSetupCriteriaUpdateRequest {
  criteria: TryoutSetupCriterion[]
}

export interface TryoutSetupRolesResponse {
  roles: TryoutRegistrationRole[]
  criteria?: TryoutSetupCriterion[]
  roleCriteria?: TryoutSetupRoleCriterionLink[]
  validation: TryoutSetupValidation
}

export interface TryoutSetupRolesUpdateRequest {
  roles: TryoutRegistrationRole[]
  roleCriteria?: TryoutSetupRoleCriterionLink[]
}

export interface TryoutSetupUpdateResponse {
  event: TryoutSetupEvent
  sessions?: TryoutRegistrationSession[]
  questions?: TryoutRegistrationQuestion[]
  messages?: TryoutSetupMessage[]
  validation: TryoutSetupValidation
}

export interface TryoutSetupPublishResponse {
  event: TryoutSetupEvent
  validation: TryoutSetupValidation
}

export class TryoutSetupAPI {
  async loadSetupBundle(
    seasonId: string,
    tryoutId: string,
    clubId?: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupBundleResponse> {
    const query = clubId ? { clubId } : undefined
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup`, query, signal)
  }

  async updateDetails(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupDetailsUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupUpdateResponse> {
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/details`, request, undefined, signal)
  }

  async updatePublication(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupPublicationUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupUpdateResponse> {
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/publication`, request, undefined, signal)
  }

  async updateQuestions(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupQuestionUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupUpdateResponse> {
    return apiClient.put(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/questions`, request, undefined, signal)
  }

  async updateChildSafety(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupChildSafetyUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupUpdateResponse> {
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/child-safety`, request, undefined, signal)
  }

  async publish(
    seasonId: string,
    tryoutId: string,
    active: boolean,
    signal?: AbortSignal
  ): Promise<TryoutSetupPublishResponse> {
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/publication-state`, { active }, undefined, signal)
  }

  async loadFees(
    seasonId: string,
    tryoutId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupFeesResponse> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/fees`, undefined, signal)
  }

  async updateFees(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupFeesUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupFeesResponse> {
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/fees`, request, undefined, signal)
  }

  async loadAttributes(
    seasonId: string,
    tryoutId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupAttributesResponse> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/attributes`, undefined, signal)
  }

  async createAttribute(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupAttributeUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupAttributeResponse> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/attributes`, request, undefined, signal)
  }

  async updateAttribute(
    seasonId: string,
    tryoutId: string,
    attributeId: string,
    request: TryoutSetupAttributeUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupAttributeResponse> {
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/attributes/${attributeId}`, request, undefined, signal)
  }

  async deleteAttribute(
    seasonId: string,
    tryoutId: string,
    attributeId: string,
    signal?: AbortSignal
  ): Promise<void | TryoutSetupAttributesResponse> {
    return apiClient.delete(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/attributes/${attributeId}`, undefined, signal)
  }

  async updateAttributeOrder(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupAttributeOrderUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupAttributesResponse> {
    return apiClient.patch(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/attributes/order`, request, undefined, signal)
  }

  async loadSpaces(
    seasonId: string,
    tryoutId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupSpacesResponse> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/spaces`, undefined, signal)
  }

  async updateSpaces(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupSpacesUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupSpacesResponse> {
    return apiClient.put(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/spaces`, request, undefined, signal)
  }

  async loadTeams(
    seasonId: string,
    tryoutId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupTeamsResponse> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/teams`, undefined, signal)
  }

  async updateTeams(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupTeamsUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupTeamsResponse> {
    return apiClient.put(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/teams`, request, undefined, signal)
  }

  async loadStaff(
    seasonId: string,
    tryoutId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupStaffResponse> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/staff`, undefined, signal)
  }

  async updateStaff(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupStaffUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupStaffResponse> {
    return apiClient.put(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/staff`, request, undefined, signal)
  }

  async loadEmails(
    seasonId: string,
    tryoutId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupEmailsResponse> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/emails`, undefined, signal)
  }

  async updateEmails(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupEmailsUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupEmailsResponse> {
    return apiClient.put(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/emails`, request, undefined, signal)
  }

  async loadSessions(
    seasonId: string,
    tryoutId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupSessionsResponse> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/sessions`, undefined, signal)
  }

  async updateSessions(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupSessionsUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupSessionsResponse> {
    return apiClient.put(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/sessions`, request, undefined, signal)
  }

  async loadCriteria(
    seasonId: string,
    tryoutId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupCriteriaResponse> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/criteria`, undefined, signal)
  }

  async updateCriteria(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupCriteriaUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupCriteriaResponse> {
    return apiClient.put(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/criteria`, request, undefined, signal)
  }

  async loadRoles(
    seasonId: string,
    tryoutId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupRolesResponse> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/roles`, undefined, signal)
  }

  async loadRubrics(
    seasonId: string,
    tryoutId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupRubricsResponse> {
    return apiClient.get(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/rubrics`, undefined, signal)
  }

  async loadRubric(
    seasonId: string,
    tryoutId: string,
    rubricId: string,
    options: TryoutSetupRubricLoadOptions = {},
    signal?: AbortSignal
  ): Promise<TryoutSetupRubricResponse> {
    const params = new URLSearchParams()
    if (options.status) params.set('status', options.status)
    if (options.versionId) params.set('versionId', options.versionId)
    const query = params.toString()
    return apiClient.get(
      `/v2/season/${seasonId}/tryouts/${tryoutId}/setup/rubrics/${rubricId}/loadRubric${query ? `?${query}` : ''}`,
      undefined,
      signal
    )
  }

  async inheritRubric(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupRubricInheritRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupRubricResponse> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/rubrics/inherit`, request, undefined, signal)
  }

  async createRubric(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupRubricUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupRubricResponse> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/rubrics`, request, undefined, signal)
  }

  async updateRubric(
    seasonId: string,
    tryoutId: string,
    rubricId: string,
    request: TryoutSetupRubricUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupRubricResponse> {
    return apiClient.put(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/rubrics/${rubricId}`, request, undefined, signal)
  }

  async createRubricDraft(
    seasonId: string,
    tryoutId: string,
    rubricId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupRubricResponse> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/rubrics/${rubricId}/draft`, {}, undefined, signal)
  }

  async publishRubric(
    seasonId: string,
    tryoutId: string,
    rubricId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupRubricResponse> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/rubrics/${rubricId}/publish`, {}, undefined, signal)
  }

  async discardRubricDraft(
    seasonId: string,
    tryoutId: string,
    rubricId: string,
    signal?: AbortSignal
  ): Promise<TryoutSetupRubricResponse> {
    return apiClient.post(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/rubrics/${rubricId}/draft/discard`, {}, undefined, signal)
  }

  async updateRoles(
    seasonId: string,
    tryoutId: string,
    request: TryoutSetupRolesUpdateRequest,
    signal?: AbortSignal
  ): Promise<TryoutSetupRolesResponse> {
    return apiClient.put(`/v2/season/${seasonId}/tryouts/${tryoutId}/setup/roles`, request, undefined, signal)
  }
}

export const tryoutSetupAPI = new TryoutSetupAPI()
